import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import {
  isBefore,
  differenceInDays,
  startOfDay,
  endOfDay,
  isSameDay,
  subMonths,
  parseISO,
} from 'date-fns';

export class CustomValidator {
  // パスワードと確認用パスワードが一致するかチェック
  static matchPassword(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!(control instanceof FormGroup)) return null;

      const passwordControl = control.get('password');
      const confirmPasswordControl = control.get('passwordConfirm');

      if (!passwordControl || !confirmPasswordControl) {
        return null;
      }

      if (
        confirmPasswordControl.errors &&
        !confirmPasswordControl.errors['mismatch']
      ) {
        return null;
      }

      if (passwordControl.value !== confirmPasswordControl.value) {
        confirmPasswordControl.setErrors({ mismatch: true });
        return { mismatch: true };
      } else {
        confirmPasswordControl.setErrors(null);
        return null;
      }
    };
  }

  // 変更前のパスワードとパスワードが異なるかチェック
  static diffPassword(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!(control instanceof FormGroup)) return null;

      const oldPasswordControl = control.get('oldPassword');
      const passwordControl = control.get('password');

      if (!oldPasswordControl || !passwordControl) {
        return null;
      }

      if (passwordControl.errors && !passwordControl.errors['samePassword']) {
        return null;
      }

      if (oldPasswordControl.value === passwordControl.value) {
        passwordControl.setErrors({ samePassword: true });
        return { samePassword: true };
      } else {
        passwordControl.setErrors(null);
        return null;
      }
    };
  }

  static startDateBeforeEndDateValidator(
    fromControlName: string,
    toControlName: string,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const startDateControl = control.get(fromControlName);
      const startDate = startDateControl?.value;

      const endDateControl = control.get(toControlName);
      const endDate = endDateControl?.value;

      // 開始日時、終了日時の値としては問題なく、日時の関係が逆転している時のみエラーにする
      if (
        (!startDate && !endDate) ||
        startDateControl?.errors ||
        endDateControl?.errors ||
        // 開始日時と終了日時が同じ値の時は問題ないと判定するため、startOfDay, endOfDayを使用する
        (startDate &&
          endDate &&
          isBefore(startOfDay(startDate), endOfDay(endDate)))
      ) {
        return null;
      } else {
        return { startDateBeforeEndDate: true };
      }
    };
  }

  static startDateTimeBeforeEndDatetimeValidator(
    fromControlName: string,
    toControlName: string,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const startDateControl = control.get(fromControlName);
      const startDatetime = startDateControl?.value;

      const endDateControl = control.get(toControlName);
      const endDatetime = endDateControl?.value;

      // 開始日時、終了日時の値としては問題なく、日時の関係が逆転している時のみエラーにする
      if (
        (!startDatetime && !endDatetime) ||
        startDateControl?.errors ||
        endDateControl?.errors ||
        (startDatetime && endDatetime && isBefore(startDatetime, endDatetime))
      ) {
        return null;
      } else {
        return { startDateBeforeEndDatetime: true };
      }
    };
  }

  static startDateTimeBeforeOptionalEndDatetimeValidator(
    fromControlName: string,
    toControlName: string,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const startDateControl = control.get(fromControlName);
      const startDatetime = startDateControl?.value;

      const endDateControl = control.get(toControlName);
      const endDatetime = endDateControl?.value;

      // 開始日時、終了日時の値としては問題なく、日時の関係が逆転している時のみエラーにする
      if (
        !startDatetime ||
        !endDatetime ||
        startDateControl?.errors ||
        endDateControl?.errors ||
        (startDatetime && endDatetime && isBefore(startDatetime, endDatetime))
      ) {
        return null;
      } else {
        return { startDateBeforeOptionalEndDatetime: true };
      }
    };
  }

  static datetimeBeforeNowDateValidator(): ValidatorFn {
    return (
      dateControl: AbstractControl,
    ): { [key: string]: boolean } | null => {
      const date = dateControl.value;

      const now = new Date();
      // 開始日時、終了日時の値としては問題なく、現在日時よりのみエラーにする
      if (!date || (date && isBefore(now, date))) {
        return null;
      } else {
        return { datetimeBeforeNowDateTime: true };
      }
    };
  }

  // 複数の日時範囲指定のうち1つは必須で設定されているかをチェックする
  static requireMultiDateRangeValidator(
    controlNameList: string[],
    otherGroupList: FormGroup[] = [],
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      // 自身をチェック
      const requireDateRange = this.requireDateRangeValidator(
        control,
        controlNameList,
      );

      // 他のグループの中ですべてエラーでるかをチェック
      const requireOtherDateRange = otherGroupList.every((otherGroup) =>
        this.requireDateRangeValidator(otherGroup, controlNameList),
      );

      if (requireDateRange && requireOtherDateRange) {
        return { requireMultiDateRange: true };
      } else {
        return null;
      }
    };
  }

  static earlyDateValidator(months: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const selectedDatetime = control.value;
      const monthsAgo = startOfDay(subMonths(new Date(), months));
      if (selectedDatetime && isBefore(selectedDatetime, monthsAgo)) {
        return { dateTooEarly: true };
      }
      return null;
    };
  }

  static NotBeforeCreationDateValidator(
    tenantCreatedAt: string | null,
  ): ValidatorFn {
    if (!tenantCreatedAt) {
      return () => null;
    }
    const tenantCreatedAtDate = parseISO(tenantCreatedAt);
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const selectedDatetime = control.value;
      if (selectedDatetime && isBefore(selectedDatetime, tenantCreatedAtDate)) {
        return { dateBeforeCreation: true };
      }
      return null;
    };
  }

  private static requireDateRangeValidator(
    control: AbstractControl,
    controlNameList: string[],
  ): boolean {
    const existedDate = controlNameList.some((controlName) => {
      const dateControl = control.get(controlName);
      return dateControl?.value;
    });

    const existedOtherErrors = controlNameList.some((controlName) => {
      const dateControl = control.get(controlName);
      return dateControl?.errors;
    });

    return !existedDate && !existedOtherErrors;
  }

  static maxRangeDaysValidator(
    fromControlName: string,
    toControlName: string,
    maxRangeDays: number,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const startDateControl = control.get(fromControlName);
      const startDate = startDateControl?.value;

      const endDateControl = control.get(toControlName);
      const endDate = endDateControl?.value;

      // 開始日時、終了日時の値としては問題なく、日時の関係が逆転している時のみエラーにする
      if (
        (!startDate && !endDate) ||
        startDateControl?.errors ||
        endDateControl?.errors ||
        (startDate &&
          endDate &&
          // 開始日時と終了日時の差を正しく判定するためにstartOfDay, endOfDayを使用する
          // startOfDay, endOfDayがなかった場合は、maxRangeDaysを7に設定していたとしても8日の幅で指定することを許してしまう時が出てしまう
          // (differenceInDaysは日付の差を切り捨てで取得するため)
          differenceInDays(endOfDay(endDate), startOfDay(startDate)) <=
            maxRangeDays)
      ) {
        return null;
      } else {
        return { maxRangeExceeded: true };
      }
    };
  }

  static maxMonthsValidator(
    fromControlName: string,
    toControlName: string,
    numberOfMonths: number,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const toDateControl = control.get(toControlName);
      const toDate: Date | undefined = toDateControl?.value;

      const fromDateControl = control.get(fromControlName);
      const fromDate: Date | undefined = fromDateControl?.value;
      if (!fromDate || !toDate) {
        return null;
      }
      const maxDate = new Date(fromDate.getTime());

      maxDate.setMonth(maxDate.getMonth() + numberOfMonths);

      if (toDate > endOfDay(maxDate)) {
        return { maxMonths: true };
      }
      return null;
    };
  }

  static sameDayArrayValidator(
    controlName: string,
    formArrayName: string,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const dateControl = control.get(controlName);
      const baseDate: Date | undefined = dateControl?.value;

      if (!baseDate) {
        return null;
      }

      let foundError = false;
      const formArray = control.get(formArrayName) as FormArray;
      for (const controlItem of formArray.controls) {
        if (controlItem.errors) continue;

        const dateItem: Date | undefined = controlItem.value;
        if (dateItem && isSameDay(dateItem, baseDate)) {
          controlItem.setErrors({ sameDay: true });
          foundError = true;
        } else {
          controlItem.setErrors(null);
        }
      }

      if (foundError) {
        formArray.setErrors({ sameDay: true });
        return { sameDay: true };
      } else {
        formArray.setErrors(null);
        return null;
      }
    };
  }
}
